/* eslint max-lines: ["error", {"max": 300, "skipComments": true}] */

import querystring from 'querystring';
import cloneDeep from 'lodash/cloneDeep';
import makeQueryParamsUtil from '../../../../../shared/makeQueryParamsUtil';
import { formatWatcherFiltersTags } from '../../search-utils';
import { expendFlattenedAggregationsForRequest } from './aggregationsUtils';
import { getParties } from '../../../watchers/getParties';
import {
  aggregationSeparator,
  defaultPaginationSize,
  minimalQueryLength,
} from './constants';
import { StateType } from '../../../state/state.types';
import { SearchParams, CategoryEnum } from '../../search.types';
import { buildAggregationFilters } from '../../components/search-snippets/aside/filter-selected/searchFiltersUtils';

type QueryParamsStateType = ReturnType<typeof getQueryParamsFromState>;
export function getQueryParamsFromState(queryParams: StateType['queryParams']) {
  const params = {
    query: queryParams.userQuery,
    chrono: queryParams.sort === 'chrono' || queryParams.sort === 'antiChrono',
    sort_nbr_commentaire: queryParams.sort === 'commented',
    chrono_inverted: queryParams.sort === 'antiChrono',
    from: queryParams.from,
    size: queryParams.size,
    date_from: queryParams.dates.from,
    date_to: queryParams.dates.to,
    date_exact: queryParams.dates.exact,
    lawyer_id: queryParams.lid,
    enterprise_id: queryParams.eid,
    law_office_id: queryParams.oid,
    disable_autocorrect: queryParams.disableAutocorrect,
    type: queryParams.type,
    original_query_key: queryParams.originalQueryKey,
    only_top_results: queryParams.onlyTopResults,
    party: queryParams.parties.plaintiff,
    other_party: queryParams.parties.defendant,
    exclude_moyens: queryParams.excludeMoyensAnnexes,
    restrict_to_law_article_ids: queryParams.restrictToLawArticleIds,
    cats: expendFlattenedAggregationsForRequest(queryParams.aggregationsPaths),
    selected_keywords: queryParams.selectedKeywords,
  };

  delete params.original_query_key;
  return params;
}

function makeURLFromQueryUtil(
  searchQuery: QueryParamsStateType,
  pathname: string = '/search',
) {
  const params = makeQueryParamsUtil(searchQuery);

  return `${pathname}?${querystring.stringify(params)}`;
}

export function getSearchQueryParamsFromState(
  queryParams: StateType['queryParams'],
): SearchParams {
  const params: SearchParams = convertSearchParams(queryParams);

  const aggregations = expandAggregations(queryParams.aggregationsPaths);

  return { ...aggregations, ...params };
}

export function convertSearchParams(
  queryParams: StateType['queryParams'],
): SearchParams {
  const params: SearchParams = {
    q: queryParams.userQuery,
    chrono: queryParams.sort === 'chrono' || queryParams.sort === 'antiChrono',
    sort_nbr_commentaire: queryParams.sort === 'commented',
    chrono_inverted: queryParams.sort === 'antiChrono',
    from: queryParams.from,
    size: queryParams.size,
    date_from: queryParams.dates.from,
    date_to: queryParams.dates.to,
    date_exact: queryParams.dates.exact,
    lid: queryParams.lid,
    eid: queryParams.eid,
    oid: queryParams.oid,
    disable_autocorrect: queryParams.disableAutocorrect,
    type: queryParams.type,
    original_query_key: queryParams.originalQueryKey,
    only_top_results: queryParams.onlyTopResults,
    party: queryParams.parties.plaintiff,
    other_party: queryParams.parties.defendant,
    exclude_moyens: queryParams.excludeMoyensAnnexes,
    selected_keywords: queryParams.selectedKeywords,
    restrict_to_law_article_ids: queryParams.restrictToLawArticleIds,
  };

  return params;
}

export function expandAggregations(aggregationsPaths: string[]) {
  const aggregations = expendFlattenedAggregationsForRequest(aggregationsPaths);

  const result: {
    [key in CategoryEnum]?: string;
  } = {};

  (Object.keys(aggregations) as Array<keyof typeof aggregations>).forEach(
    (key) => {
      result[key] = JSON.stringify(aggregations[key]);
    },
  );
  return result;
}

const getSelectedAggregations = (selectedAggregations: Array<string>) => {
  const keys = <const>[
    'ville',
    'juridiction',
    'domaine',
    'publi',
    'solution',
    'publisher',
    'textCode',
    'otherLegislation',
    'collectiveAgreements',
    'commentaryAccess',
    'pwCategory',
    'pwLegislativeFolder',
  ];

  return keys.reduce((acc, key) => {
    return acc + buildAggregationFilters(selectedAggregations, key).length;
  }, 0);
};

export function getSelectedFilterCount({
  queryParams,
}: {
  queryParams: StateType['queryParams'];
}) {
  let sum = 0;
  if (queryParams.type === 'lawyer') {
    return 0;
  }

  if (queryParams.excludeMoyensAnnexes) {
    sum += 1;
  }

  if (queryParams.dates.from || queryParams.dates.to) {
    sum += 1;
  }
  sum += getSelectedAggregations(queryParams.aggregationsPaths);

  if (
    queryParams.parties.plaintiff != null ||
    queryParams.parties.defendant != null
  ) {
    sum += 1;
  }
  if (queryParams.restrictToLawArticleIds)
    sum += queryParams.restrictToLawArticleIds.length;
  if (queryParams.eid) sum += 1;
  if (queryParams.lid) sum += 1;
  return sum;
}

function fixHistoryRewriteIssue(url: string) {
  // https://stackoverflow.com/questions/11068907/html5-history-pushstate-mangles-urls-containing-percent-encoded-non-ascii-unic
  return url
    .replace(/%40/gi, '@')
    .replace(/%3A/gi, ':')
    .replace(/%24/g, '$')
    .replace(/%2C/gi, ',')
    .replace(/%3B/gi, ';')
    .replace(/%20/g, '%20');
}

export function buildUrlFromSearchQuery(
  params: QueryParamsStateType,
  pathname: string = '/search',
) {
  return fixHistoryRewriteIssue(makeURLFromQueryUtil(params, pathname));
}

export function buildSearchUrl(
  queryParams: StateType['queryParams'],
  pathname: string = '/search',
) {
  const params = getQueryParamsFromState(queryParams);
  return buildUrlFromSearchQuery(params, pathname);
}

function flattenPathWithSeperator(nodes: string[]) {
  return nodes.reduce((acc, _, index) => {
    acc.push(nodes.slice(0, index + 1).join(aggregationSeparator));
    return acc;
  }, <string[]>[]);
}

export function getUpdatedStateFromAdvice(
  queryParams: StateType['queryParams'],
  advice: NonNullable<StateType['results']['adviceAction']>,
) {
  const { query, cats } = advice;
  const stateCopy = cloneDeep(queryParams);

  const newState = {
    ...stateCopy,
    from: 0,
    size: defaultPaginationSize,
    originalQueryKey: undefined,
  };

  if (query) {
    newState.userQuery = query;
  }

  if (cats && Object.keys(cats).length) {
    (Object.keys(cats) as Array<keyof typeof cats>).forEach((key) => {
      cats[key]!.forEach((path) => {
        const pathNodes = [key as string].concat(path);

        flattenPathWithSeperator(pathNodes).forEach((selectedPath, index) => {
          if (
            index > 0 &&
            !stateCopy.aggregationsPaths.includes(selectedPath)
          ) {
            newState.aggregationsPaths.push(selectedPath);
          }
        });
      });
    });
  }

  if (advice.type === 'enterprise') {
    newState.eid = advice.enterpriseId;
  }
  if (advice.type === 'lawyer') {
    newState.lid = advice.lawyerId;
  }
  return newState;
}

export function getWatcherTagsFromState(
  queryParams: StateType['queryParams'],
  results: StateType['results'],
) {
  let watcherTags: Array<string> = [];

  if (queryParams.excludeMoyensAnnexes) {
    watcherTags.push('Exclure les moyens annexés');
  }

  const { defendant, plaintiff } = queryParams.parties;
  const parties = getParties({
    party: plaintiff,
    otherParty: defendant,
  });
  if (parties) {
    watcherTags.push(parties);
  }

  const cats = expendFlattenedAggregationsForRequest(
    queryParams.aggregationsPaths,
  );
  if (results.query && cats) {
    watcherTags = watcherTags.concat(formatWatcherFiltersTags(cats));
  }
  return watcherTags;
}

function hasTheRightToSend(state: StateType) {
  const isQuotaExceeded = state.user.quotaExceeded;
  const isTrialExpired = state.user.trialExpired;
  return isQuotaExceeded || isTrialExpired;
}

export function preventExtraAutocompleteRequest(state: StateType) {
  const isQueryTooShort =
    state.queryParams.userQuery.length > 0 &&
    state.queryParams.userQuery.length <= minimalQueryLength;

  return isQueryTooShort || hasTheRightToSend(state);
}
